import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Search, Users, MoreHorizontal, Eye, Trash2, Plus, Upload, Download, AlertCircle, Edit2, X } from 'lucide-react';
import Papa from 'papaparse';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api, { BACKEND_URL } from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const teamSchema = z.object({
  name:       z.string().min(2, 'Team name must be at least 2 characters'),
  short_name: z.string().max(5, 'Code must be 5 chars or less').optional().or(z.literal('')),
});

export default function TeamsPage() {
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin = isAdmin();
  const navigate = useNavigate();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const [teams, setTeams] = useState([]);
  const [teamSponsors, setTeamSponsors] = useState({});
  const [tournaments, setTournaments]   = useState([]);
  const [sponsorTid, setSponsorTid]     = useState('');
  const [bulkTeamOpen, setBulkTeamOpen] = useState(false);
  const [bulkTeamFile, setBulkTeamFile] = useState(null);
  const [bulkTeamPreview, setBulkTeamPreview] = useState([]);
  const [bulkTeamUploading, setBulkTeamUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  // Player-to-team search
  const [playerSearch, setPlayerSearch]         = useState('');
  const [playerSuggestions, setPlayerSuggestions] = useState([]);
  const [playerTeamFilter, setPlayerTeamFilter]   = useState(null); // {player_name, team_id}
  const [showPlayerSug, setShowPlayerSug]         = useState(false);
  const playerSugRef = useRef(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoB64, setLogoB64] = useState(null);
  const [teamColor, setTeamColor] = useState('#3b82f6');
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [koAchievements, setKoAchievements] = useState({}); // teamId → { sport_code: 'winner'|'runner_up'|'semi' }
  const logoFileRef = useRef(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(teamSchema),
    defaultValues: { name: '', short_name: '' },
  });

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setLogoPreview(ev.target.result); setLogoB64(ev.target.result); };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    reset();
    setLogoPreview(null);
    setLogoB64(null);
    setTeamColor('#3b82f6');
  };

  // Player-to-team search: fetch suggestions when 3+ chars typed
  useEffect(() => {
    if (playerSearch.length < 3) { setPlayerSuggestions([]); setShowPlayerSug(false); return; }
    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('btccp_access_token');
        const res = await fetch(`/api/orgs/${orgSlug}/players?q=${encodeURIComponent(playerSearch)}&limit=8`,
          { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const players = await res.json();
          setPlayerSuggestions(players);
          setShowPlayerSug(players.length > 0);
        }
      } catch (_) {}
    }, 300);
    return () => clearTimeout(timer);
  }, [playerSearch, orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectPlayerSearch = async (player) => {
    const name = player.full_name || `${player.first_name||''} ${player.last_name||''}`.trim();
    setPlayerSearch(name);
    setShowPlayerSug(false);
    setPlayerSuggestions([]); // prevent debounce from re-opening dropdown
    // Find which team this player is on via assignments
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/player-team-assignments`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const assignments = await res.json();
        const a = assignments[player.id];
        setPlayerTeamFilter(a?.team_id ? { player_name: name, team_id: a.team_id } : { player_name: name, team_id: null });
      }
    } catch (_) { setPlayerTeamFilter({ player_name: name, team_id: null }); }
  };

  const clearPlayerSearch = () => { setPlayerSearch(''); setPlayerSuggestions([]); setPlayerTeamFilter(null); };

  useEffect(() => {
    fetchTeams();
    fetchTournaments();
    fetchKOAchievements();
  }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (sponsorTid) fetchTeamSponsors(sponsorTid); else setTeamSponsors({}); }, [sponsorTid]);

  const fetchTournaments = async () => {
    try {
      const res = await api.get(`/orgs/${orgSlug}/tournaments`);
      const data = Array.isArray(res.data) ? res.data : [];
      setTournaments(data);
      if (data.length > 0) setSponsorTid(data[0].id);
    } catch (_) {}
  };

  const fetchTeamSponsors = async (tid) => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${tid}/team-sponsors`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setTeamSponsors(await res.json());
    } catch (_) {}
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orgs/${orgSlug}/teams`);
      setTeams(Array.isArray(response.data) ? response.data : response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const fetchKOAchievements = async () => {
    try {
      const token = localStorage.getItem('btccp_access_token');
      const res = await fetch(`/api/orgs/${orgSlug}/scorer/encounters`,
        { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) return;
      const encs = await res.json();
      const byTeam = {};
      (Array.isArray(encs) ? encs : [])
        .filter(e => ['sf','final','3rd'].includes(e.round_type) && e.winner)
        .forEach(e => {
          const sc = e.sport_code || e.sport_results?.[0]?.sport_code;
          if (!sc) return;
          for (const [tid, side] of [[e.home_team_id,'home'],[e.away_team_id,'away']]) {
            if (!tid) continue;
            if (!byTeam[tid]) byTeam[tid] = {};
            const won = e.winner === side;
            if (e.round_type === 'final') {
              byTeam[tid][sc] = won ? 'winner' : 'runner_up';
            } else if (e.round_type === 'sf' && !won && !byTeam[tid][sc]) {
              byTeam[tid][sc] = 'semi';
            }
          }
        });
      setKoAchievements(byTeam);
    } catch (_) {}
  };

  const fetchRoster = async (teamId) => {
    try {
      setRosterLoading(true);
      const response = await api.get(`/orgs/${orgSlug}/teams/${teamId}/roster`);
      setRoster(Array.isArray(response.data) ? response.data : response.data.players || []);
    } catch (error) {
      console.error('Failed to fetch roster:', error);
      setRoster([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const handleViewRoster = async (team) => {
    setSelectedTeam(team);
    await fetchRoster(team.id);
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Are you sure you want to delete this team?')) return;
    try {
      await api.delete(`/orgs/${orgSlug}/teams/${teamId}`);
      toast.success('Team deleted successfully');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(teamId); return n; });
      fetchTeams();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  const toggleSelectTeam = (id) =>
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleSelectAll = () =>
    setSelectedIds(selectedIds.size === filteredTeams.length && filteredTeams.length > 0
      ? new Set()
      : new Set(filteredTeams.map(t => t.id)));

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    if (!count) return;
    if (!confirm(`Delete ${count} team${count > 1 ? 's' : ''}? This will also remove their rosters and cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      await Promise.all([...selectedIds].map(id => api.delete(`/orgs/${orgSlug}/teams/${id}`)));
      toast.success(`${count} team${count > 1 ? 's' : ''} deleted`);
      setSelectedIds(new Set());
      fetchTeams();
    } catch {
      toast.error('Some deletions failed — refresh and try again');
      fetchTeams();
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleCreateTeam = async (data) => {
    setCreating(true);
    try {
      // Step 1: Create team
      const token = localStorage.getItem('btccp_access_token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      const res = await fetch(`/api/orgs/${orgSlug}/teams`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: data.name, short_name: data.short_name || undefined, color: teamColor }),
      });
      const teamData = await res.json();
      if (!res.ok) { toast.error(teamData?.detail || 'Failed to create team'); return; }

      // Step 2: Upload logo if provided
      if (logoB64 && teamData.id) {
        await fetch(`/api/orgs/${orgSlug}/teams/${teamData.id}`, {
          method: 'PATCH', headers,
          body: JSON.stringify({ logo_b64: logoB64 }),
        }).catch(() => {});
      }

      toast.success('Team created successfully');
      setCreateOpen(false);
      resetForm();
      fetchTeams();
    } catch (error) {
      toast.error('Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleTeamTemplateDownload = () => {
    const csv = ['Team Name,Short Code,Color\nCircles Kings,CK,#ef4444\nCircles Queens,CQ,#3b82f6'].join('');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'BTCCP_Teams_Template.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Teams template downloaded');
  };

  const handleExportTeams = async () => {
    try {
      toast.info('Fetching team rosters...');
      const token = localStorage.getItem('btccp_access_token');
      // Fetch rosters for all teams in parallel
      const rosterResults = await Promise.allSettled(
        filteredTeams.map(t =>
          fetch(`/api/orgs/${orgSlug}/teams/${t.id}/roster`,
            { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : [])
            .then(players => ({ teamId: t.id, players }))
        )
      );
      const rosterMap = {};
      rosterResults.forEach(r => { if (r.status === 'fulfilled') rosterMap[r.value.teamId] = r.value.players; });

      const header = ['Team Name', 'Short Code', 'Color', 'Sponsor', 'Player Name', 'Category', 'Sports', 'Platinum', 'Jersey No'];
      const rows = [];
      filteredTeams.forEach(t => {
        const players = rosterMap[t.id] || [];
        if (players.length === 0) {
          // Team with no players — still include one row
          rows.push([`"${(t.name||'').replace(/"/g,'""')}"`, t.short_name||'', t.color||'', `"${(t.sponsor_name||'').replace(/"/g,'""')}"`, '', '', '', '', ''].join(','));
        } else {
          players.forEach(p => {
            const name = p.player_name || p.player?.full_name || '';
            const category = p.category || p.player?.category || '';
            const sports = (p.sports || p.player?.sports || []).join(' | ');
            const platinum = p.is_platinum ? 'Yes' : '';
            const jersey = p.jersey_number || '';
            rows.push([
              `"${(t.name||'').replace(/"/g,'""')}"`,
              t.short_name || '',
              t.color || '',
              `"${(t.sponsor_name||'').replace(/"/g,'""')}"`,
              `"${name.replace(/"/g,'""')}"`,
              category,
              `"${sports}"`,
              platinum,
              jersey,
            ].join(','));
          });
        }
      });

      const csv = '\uFEFF' + [header.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BTCCP_Teams_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      const totalPlayers = rows.length;
      toast.success(`${filteredTeams.length} teams · ${totalPlayers} players exported`);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    }
  };

  const handleBulkTeamFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBulkTeamFile(file);
    Papa.parse(file, { header: true, skipEmptyLines: 'greedy',
      transformHeader: h => h.trim().toLowerCase().replace(/\s+/g,'_').replace(/[^\w]/g,''),
      complete: r => setBulkTeamPreview(r.data.slice(0,5)) });
  };

  const handleBulkTeamUpload = async () => {
    if (!bulkTeamFile) { toast.error('Select a CSV file'); return; }
    setBulkTeamUploading(true);
    Papa.parse(bulkTeamFile, { header: true, skipEmptyLines: 'greedy',
      transformHeader: h => h.trim().toLowerCase().replace(/\s+/g,'_').replace(/[^\w]/g,''),
      complete: async (results) => {
        try {
          const teams = results.data.map(row => ({
            name: (row.team_name || row.name || '').trim(),
            short_name: (row.short_code || row.short_name || row.code || '').trim().slice(0,5),
            color: row.color || '#3b82f6',
          })).filter(t => t.name);
          if (!teams.length) { toast.error('No valid teams found. CSV needs "Team Name" column.'); setBulkTeamUploading(false); return; }
          const token = localStorage.getItem('btccp_access_token');
          const res = await fetch(`/api/orgs/${orgSlug}/teams/bulk`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ teams }),
          });
          const data = await res.json();
          if (res.ok) {
            toast.success(`${data.created} teams uploaded`);
            setBulkTeamOpen(false); setBulkTeamFile(null); setBulkTeamPreview([]);
            fetchTeams();
          } else { toast.error(data.detail || 'Upload failed'); }
        } catch (err) { toast.error(`Error: ${err.message}`); }
        finally { setBulkTeamUploading(false); }
      }
    });
  };

  const filteredTeams = teams.filter(team => {
    if (playerTeamFilter) {
      // Player search active: show only the team this player belongs to
      return playerTeamFilter.team_id
        ? team.id === playerTeamFilter.team_id
        : false; // player found but not assigned to any team
    }
    return team.name?.toLowerCase().includes(search.toLowerCase()) ||
           team.short_name?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-teams-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Teams</h1>
          <p className="text-[#8a93a3]">Manage tournament teams and rosters</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={handleExportTeams} variant="outline" size="sm"
            className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
            data-testid="export-teams-button">
            <Download className="h-4 w-4 mr-2"/>Export
          </Button>
          {admin && (
            <>
              <Button onClick={handleTeamTemplateDownload} variant="outline" size="sm"
                className="border-[#252a37] text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26]">
                <Download className="h-4 w-4 mr-2"/>Get Template
              </Button>
              <Button onClick={() => setBulkTeamOpen(true)} variant="outline" size="sm"
                className="border-[#252a37] text-[#f0f2f7] hover:bg-[#161a26]"
                data-testid="teams-bulk-upload-button">
                <Upload className="h-4 w-4 mr-2"/>Upload CSV
              </Button>
              <Button onClick={() => setCreateOpen(true)} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                <Plus className="h-4 w-4 mr-2"/>Add Team
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Search + Tournament sponsor filter */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPlayerTeamFilter(null); }}
            placeholder="Search teams..."
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
            disabled={!!playerTeamFilter}
          />
        </div>
        {tournaments.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#8a93a3] whitespace-nowrap">Sponsors from:</span>
            <select value={sponsorTid} onChange={e => setSponsorTid(e.target.value)}
              className="bg-[#161a26] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm">
              <option value="">No sponsors</option>
              {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Player-to-team finder */}
      <div className="mb-6" ref={playerSugRef}>
        <div className="relative max-w-md">
          <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]"/>
          <Input
            value={playerSearch}
            onChange={e => { setPlayerSearch(e.target.value); setPlayerTeamFilter(null); }}
            onFocus={() => playerSuggestions.length > 0 && setShowPlayerSug(true)}
            placeholder="Find player's team… (type 3+ letters)"
            className="pl-10 pr-8 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#4a5568] focus:border-[#f5c518] focus-visible:ring-0"
            data-testid="player-team-search-input"
          />
          {playerSearch && (
            <button onClick={clearPlayerSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a93a3] hover:text-[#f0f2f7]">
              <X className="h-3.5 w-3.5"/>
            </button>
          )}
          {/* Dropdown */}
          {showPlayerSug && playerSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-[#1d2230] border border-[#252a37] rounded-xl shadow-xl overflow-hidden">
              {playerSuggestions.map(p => {
                const name = p.full_name || `${p.first_name||''} ${p.last_name||''}`.trim();
                return (
                  <button key={p.id}
                    onMouseDown={e => { e.preventDefault(); handleSelectPlayerSearch(p); }}
                    className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#252a37] transition-colors">
                    <div className="h-7 w-7 rounded-lg flex-shrink-0 flex items-center justify-center font-bold text-[11px] text-white bg-[#3b82f6]">
                      {name[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#f0f2f7]">{name}</p>
                      {p.sports?.length > 0 && <p className="text-[10px] text-[#8a93a3]">{p.sports.join(', ')}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {/* Result banner */}
        {playerTeamFilter && (
          <div className={`mt-2 px-3 py-2 rounded-xl text-xs flex items-center gap-2 ${
            playerTeamFilter.team_id
              ? 'bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.25)] text-[#22c55e]'
              : 'bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.25)] text-[#ef4444]'
          }`}>
            {playerTeamFilter.team_id
              ? `✓ ${playerTeamFilter.player_name} is in the highlighted team`
              : `${playerTeamFilter.player_name} is not assigned to any team`}
            <button onClick={clearPlayerSearch} className="ml-auto text-current hover:opacity-70">✕</button>
          </div>
        )}
      </div>

      {/* Bulk select bar — admins only */}
      {admin && !loading && filteredTeams.length > 0 && (
        <div className={`flex items-center gap-3 mb-4 px-1 transition-all ${selectedIds.size > 0 ? 'opacity-100' : 'opacity-50 hover:opacity-80'}`}>
          <input type="checkbox"
            checked={selectedIds.size === filteredTeams.length && filteredTeams.length > 0}
            ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < filteredTeams.length; }}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-[#252a37] bg-[#1d2230] accent-[#ef4444] cursor-pointer"
            data-testid="select-all-teams-checkbox"/>
          <span className="text-sm flex-1">
            {selectedIds.size > 0
              ? <span className="text-[#f0f2f7] font-medium">{selectedIds.size} of {filteredTeams.length} selected</span>
              : <span className="text-[#8a93a3]">Select teams to bulk delete</span>}
          </span>
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} disabled={bulkDeleting}
              className="flex items-center gap-1.5 text-sm text-[#ef4444] hover:text-[#f87171] border border-[rgba(239,68,68,0.35)] bg-[rgba(239,68,68,0.08)] hover:bg-[rgba(239,68,68,0.15)] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              data-testid="bulk-delete-teams-button">
              <Trash2 className="h-4 w-4"/>
              {bulkDeleting ? 'Deleting...' : `Delete ${selectedIds.size} Team${selectedIds.size > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      )}

      {/* Teams Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="bg-[#161a26] border-[#252a37]">
              <CardContent className="p-4">
                <Skeleton className="h-12 w-12 rounded-full bg-[#1d2230] mb-3" />
                <Skeleton className="h-5 w-3/4 bg-[#1d2230] mb-2" />
                <Skeleton className="h-4 w-1/2 bg-[#1d2230]" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTeams.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="teams-table">
          {filteredTeams.map((team) => {
            const isSelected = selectedIds.has(team.id);
            return (
            <Card
              key={team.id}
              className={`border transition-all cursor-pointer hover:-translate-y-0.5 ${
                isSelected
                  ? 'bg-[rgba(239,68,68,0.06)] border-[rgba(239,68,68,0.4)] ring-1 ring-[rgba(239,68,68,0.25)]'
                  : 'bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.35)]'
              }`}
              onClick={() => navigate(`/admin/teams/${team.id}`)}
            >
              <CardContent className="p-5 flex flex-col items-center text-center relative">
                {/* Selection checkbox — admins only */}
                {admin && (
                <div className="absolute top-2 left-2" onClick={e => e.stopPropagation()}>
                  <input type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelectTeam(team.id)}
                    className="h-4 w-4 rounded border-[#252a37] bg-[#1d2230] accent-[#ef4444] cursor-pointer"
                    data-testid={`team-checkbox-${team.id}`}
                  />
                </div>
                )}
                {/* Large colored circle with initials */}
                <div className="h-16 w-16 rounded-full flex items-center justify-center font-bold text-white text-xl mb-3 overflow-hidden flex-shrink-0"
                  style={{ backgroundColor: team.color || '#3b82f6' }}>
                  {team.logo_url?.startsWith('data:') ? (
                    <img src={team.logo_url} className="h-full w-full object-cover" alt={team.name}/>
                  ) : (
                    team.short_name || (team.name?.split(' ').filter(Boolean).map(w=>w[0]).join('').toUpperCase().slice(0,3))
                  )}
                </div>
                <p className="font-bold text-[#f0f2f7] text-sm leading-tight mb-1">{team.name}</p>
                <p className="text-xs text-[#8a93a3] flex items-center gap-1 mb-1">
                  <Users className="h-3 w-3"/>
                  {team.player_count || 0} players
                </p>
                {teamSponsors[team.id] && (
                  <p className="text-[10px] text-[#f5c518] font-medium truncate max-w-full" title={`Sponsor: ${teamSponsors[team.id].sponsor_name}`}>
                    ★ {teamSponsors[team.id].sponsor_name}
                  </p>
                )}
                {/* Sport achievement badges */}
                {koAchievements[team.id] && Object.keys(koAchievements[team.id]).length > 0 && (
                  <div className="flex flex-wrap justify-center gap-1 mt-1.5">
                    {Object.entries(koAchievements[team.id]).map(([sc, result]) => {
                      const cfg = {
                        winner:    { icon:'🏆', color:'#f5c518', bg:'rgba(245,197,24,0.1)', border:'rgba(245,197,24,0.35)' },
                        runner_up: { icon:'🥈', color:'#94a3b8', bg:'rgba(148,163,184,0.08)', border:'rgba(148,163,184,0.3)' },
                        semi:      { icon:'🥉', color:'#fb923c', bg:'rgba(251,146,60,0.08)', border:'rgba(251,146,60,0.3)' },
                      }[result] || {};
                      const sn = sc.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase());
                      return (
                        <span key={sc} className="text-[9px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap"
                          style={{ color:cfg.color, backgroundColor:cfg.bg, border:`1px solid ${cfg.border}` }}>
                          {cfg.icon} {sn}
                        </span>
                      );
                    })}
                  </div>
                )}
              </CardContent>
              <div className="px-4 pb-3 flex justify-end" onClick={e => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[#8a93a3] hover:text-[#f0f2f7]">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#161a26] border-[#252a37]">
                      <DropdownMenuItem
                        onClick={() => navigate(`/admin/teams/${team.id}`)}
                        className="text-[#f0f2f7] focus:bg-[#1d2230] cursor-pointer"
                        data-testid="team-roster-open-button">
                        <Eye className="h-4 w-4 mr-2"/>
                        View Team
                      </DropdownMenuItem>
                      {admin && (
                        <>
                          <DropdownMenuItem
                            onClick={() => navigate(`/admin/teams/${team.id}?edit=1`)}
                            className="text-[#f5c518] focus:bg-[#1d2230] cursor-pointer">
                            <Edit2 className="h-4 w-4 mr-2"/>
                            Edit Team
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteTeam(team.id)}
                            className="text-[#ef4444] focus:bg-[#1d2230] cursor-pointer">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete Team
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
              </div>
            </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-12 text-center">
            <Trophy className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
              {search ? 'No teams found' : 'No teams yet'}
            </h3>
            <p className="text-[#8a93a3] mb-4">
              {search 
                ? 'Try adjusting your search' 
                : 'Upload teams via the Tournament Setup wizard'}
            </p>
            {!search && (
              <Button 
                onClick={() => window.location.href = '/admin/tournament-setup'}
                className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              >
                Go to Tournament Setup
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Roster Dialog */}
      <Dialog open={!!selectedTeam} onOpenChange={() => setSelectedTeam(null)}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-2xl" data-testid="team-roster-drawer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-[#f0f2f7]">
              <Trophy className="h-5 w-5 text-[#f5c518]" />
              {selectedTeam?.name} Roster
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[400px]">
            {rosterLoading ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full bg-[#1d2230]" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-32 bg-[#1d2230] mb-1" />
                      <Skeleton className="h-3 w-24 bg-[#1d2230]" />
                    </div>
                  </div>
                ))}
              </div>
            ) : roster.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[#252a37]">
                    <TableHead className="text-[#8a93a3]">Player</TableHead>
                    <TableHead className="text-[#8a93a3]">Sport</TableHead>
                    <TableHead className="text-[#8a93a3]">Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roster.map((player, index) => (
                    <TableRow key={player.id || player.player_id || `roster-${index}`} className="border-b border-[#252a37] hover:bg-[#1d2230]/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-[#1d2230] flex items-center justify-center text-[#f5c518] font-medium">
                            {player.name?.[0] || 'P'}
                          </div>
                          <span className="text-[#f0f2f7]">{player.name || player.player_name || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-[#8a93a3]">{player.sport || '-'}</TableCell>
                      <TableCell className="text-[#8a93a3]">{player.category || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="p-8 text-center">
                <Users className="h-10 w-10 text-[#8a93a3] mx-auto mb-3" />
                <p className="text-[#8a93a3]">No players in this team yet</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="bg-[#161a26] border-[#252a37]">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7]">Add New Team</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(handleCreateTeam)} className="space-y-4">

            {/* Logo + Color row */}
            <div className="flex items-center gap-4">
              <div>
                <Label className="text-[#8a93a3] mb-2 block">Logo</Label>
                <div className="flex items-center gap-3">
                  <div
                    onClick={() => logoFileRef.current?.click()}
                    className="h-14 w-14 rounded-xl cursor-pointer overflow-hidden flex items-center justify-center border-2 border-dashed border-[#252a37] hover:border-[#f5c518] transition-colors flex-shrink-0"
                    style={{ backgroundColor: teamColor }}
                    title="Click to upload logo"
                    data-testid="team-logo-upload-area"
                  >
                    {logoPreview
                      ? <img src={logoPreview} className="h-full w-full object-cover" alt="logo" />
                      : <Upload className="h-5 w-5 text-white/70" />}
                  </div>
                  <input type="file" accept="image/*" ref={logoFileRef} hidden onChange={handleLogoChange} />
                  {logoPreview && (
                    <button type="button" onClick={() => { setLogoPreview(null); setLogoB64(null); }}
                      className="text-xs text-[#8a93a3] hover:text-[#ef4444]">Remove</button>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <Label className="text-[#8a93a3] mb-2 block">Team Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={teamColor}
                    onChange={e => setTeamColor(e.target.value)}
                    className="h-10 w-10 rounded-lg cursor-pointer border-0 p-0.5 bg-transparent"
                  />
                  <span className="text-sm text-[#8a93a3] font-mono">{teamColor}</span>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[#8a93a3]">Team Name *</Label>
              <Input
                {...register('name')}
                placeholder="e.g., Thunder Hawks"
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] mt-1"
              />
              {errors.name && <p className="text-xs text-[#ef4444] mt-1">{errors.name.message}</p>}
            </div>
            <div>
              <Label className="text-[#8a93a3]">Short Code (max 5)</Label>
              <Input
                {...register('short_name')}
                placeholder="e.g., THK"
                maxLength={5}
                className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7] uppercase mt-1"
              />
              {errors.short_name && <p className="text-xs text-[#ef4444] mt-1">{errors.short_name.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); resetForm(); }} className="border-[#252a37] text-[#f0f2f7]">
                Cancel
              </Button>
              <Button type="submit" disabled={creating} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                {creating ? 'Creating...' : 'Create Team'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Team Upload Dialog */}
      <Dialog open={bulkTeamOpen} onOpenChange={o => { setBulkTeamOpen(o); if (!o) { setBulkTeamFile(null); setBulkTeamPreview([]); } }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="teams-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#f5c518]"/>Bulk Upload Teams
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#1d2230] rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-[#f5c518] flex-shrink-0 mt-0.5"/>
              <div className="text-xs text-[#8a93a3]">
                <p>CSV columns: <code className="text-[#f5c518]">Team Name</code>, Short Code, Color</p>
                <p className="mt-1">Only Team Name is required.</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-[#8a93a3]">Choose CSV File</Label>
              <Button size="sm" variant="ghost" onClick={handleTeamTemplateDownload}
                className="text-xs text-[#f5c518] hover:text-[#f7d24a] h-7 px-2">
                <Download className="h-3 w-3 mr-1"/>Download Template
              </Button>
            </div>
            <input type="file" accept=".csv" onChange={handleBulkTeamFileChange}
              className="block w-full text-sm text-[#8a93a3] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#1d2230] file:text-[#f5c518] file:font-medium hover:file:bg-[#252a37] cursor-pointer"
              data-testid="teams-bulk-file-input"/>
            {bulkTeamPreview.length > 0 && (
              <div className="bg-[#1d2230] rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-[#252a37]">
                    {Object.keys(bulkTeamPreview[0]).map(h => <th key={h} className="px-3 py-2 text-left text-[#8a93a3]">{h}</th>)}
                  </tr></thead>
                  <tbody>{bulkTeamPreview.map((row,i) => (
                    <tr key={i} className="border-b border-[#252a37]/50">
                      {Object.values(row).map((v,j) => <td key={j} className="px-3 py-1.5 text-[#f0f2f7] truncate max-w-[100px]">{v}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkTeamOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleBulkTeamUpload} disabled={!bulkTeamFile || bulkTeamUploading}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="teams-bulk-upload-submit">
              {bulkTeamUploading ? 'Uploading...' : 'Upload Teams'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
